<template>
	<a-modal :title="isUpdate ? '修改模版' : '新增模版'" :width="920" :visible="visible" :confirmLoading="false"
		:maskClosable="false" @ok="handleOk" @cancel="handleCancel">
		<a-spin :spinning="loading">
			<a-row :gutter="20" type="flex">
				<a-col :span="14">
					<a-form-model ref="adminForm" :model="form" :label-col="{span:6}" :wrapper-col="{span:17}" autoComplete="off">
				<a-form-model-item v-show="false" label="ID" prop="id">
					<a-input type="hidden" v-model.number="form.id" />
				</a-form-model-item>
				<a-form-model-item label="场景标签" prop="tagId"
					:rules="[{ required: true, message: '请选择场景标签', trigger: 'change' }]">
					<a-spin :spinning="sceneTagFetching" size="small">
						<a-select
							v-model="form.tagId"
							show-search
							allow-clear
							:filter-option="filterSceneTagOption"
							not-found-content="暂无数据"
							placeholder="请选择场景标签，可输入关键字筛选"
							style="width: 100%"
						>
							<a-select-option
								v-for="item in sceneTagOptions"
								:key="'tag-' + item.id"
								:value="Number(item.id)"
							>
								{{ item.depict ? item.name + ' — ' + item.depict : item.name }}
							</a-select-option>
						</a-select>
					</a-spin>
				</a-form-model-item>
				<a-form-model-item label="目标性别" prop="targetSex"
					:rules="[{ required: true, message: '必填，不能为空', trigger: 'change' }]">
					<a-select v-model="form.targetSex" style="width:100px" placeholder="请选择">
						<a-select-option value="MALE">男</a-select-option>
						<a-select-option value="FEMALE">女</a-select-option>
					</a-select>
				</a-form-model-item>
				<a-form-model-item label="阶段" prop="step"
					:rules="[{ required: true, message: '必填，不能为空', trigger: 'change' }]">
					<a-select v-model="form.step" style="width:120px" placeholder="请选择">
						<a-select-option value="1">一阶段</a-select-option>
						<a-select-option value="2">二阶段</a-select-option>
						<a-select-option value="3">三阶段</a-select-option>
					</a-select>
				</a-form-model-item>
				<a-form-model-item label="内容" prop="message"
					:rules="[{ required: true, message: '必填，不能为空', trigger: 'blur' }]">
					<a-textarea
						ref="messageTextarea"
						v-model="form.message"
						placeholder="请输入模版内容"
						:auto-size="{ minRows: 6, maxRows: 14 }"
						@select="syncMessageCaret"
						@keyup="syncMessageCaret"
						@click="syncMessageCaret"
						@input="syncMessageCaret"
						@blur="syncMessageCaret"
					/>
				</a-form-model-item>
			</a-form-model>
				</a-col>
				<a-col :span="10">
					<div class="template-var-panel">
						<div class="template-var-panel-title">模板变量</div>
						<p class="template-var-panel-hint">点击变量插入到「内容」光标处；编辑内容时会自动记录光标位置。</p>
						<div
							v-for="(row, idx) in templateVariableRows"
							:key="idx"
							class="template-var-row"
						>
							<div class="template-var-tokens">
								<span
									v-for="tok in row.tokens"
									:key="tok"
									class="template-var-token"
									@mousedown.prevent="insertTemplateVariable(tok)"
								>{{ tok }}</span>
							</div>
							<div class="template-var-desc">{{ row.desc }}</div>
						</div>
					</div>
				</a-col>
			</a-row>
		</a-spin>
	</a-modal>
</template>

<script>
	import pick from 'lodash.pick';
	import {
		accostSceneTagSuggest
	} from '@/api/content/accost.js';

	const fields = ['id', 'tagId', 'message', 'targetSex', 'step'];

	/** 与产品设计一致：发起方 from* / 接收方 to* */
	const templateVariableRows = [
		{ tokens: ['{fromUserNickname}', '{toUserNickname}'], desc: '发起/接收方昵称' },
		{ tokens: ['{fromUserAge}', '{toUserAge}'], desc: '发起/接收方年龄' },
		{ tokens: ['{fromUserProvince}', '{toUserProvince}'], desc: '发起/接收方省' },
		{ tokens: ['{fromUserCity}', '{toUserCity}'], desc: '发起/接收方市' },
		{ tokens: ['{fromUserZodiac}', '{toUserZodiac}'], desc: '发起/接收方属相' },
	];

	function emptyForm() {
		return {
			id: 0,
			tagId: undefined,
			message: '',
			targetSex: undefined,
			step: undefined
		};
	}

	export default {
		props: {
			visible: {
				type: Boolean,
				required: true
			},
			loading: {
				type: Boolean,
				default: () => false
			},
			model: {
				type: Object,
				default: () => null
			}
		},
		data() {
			return {
				form: emptyForm(),
				sceneTagOptions: [],
				sceneTagFetching: false,
				templateVariableRows,
				messageCaret: { start: 0, end: 0 },
			}
		},
		beforeDestroy() {
			if (this.unwatch) {
				this.unwatch();
			}
			if (this.unwatchVisible) {
				this.unwatchVisible();
			}
		},
		created() {
			this.unwatch = this.$watch('model', (newVal) => {
				if (!newVal) {
					this.initForm();
					return;
				}

				this.$nextTick(() => {
					const base = pick(newVal, fields);
					if (base.tagId === undefined || base.tagId === null || base.tagId === '') {
						const raw = newVal.tagId != null ? newVal.tagId : newVal.tag_id;
						base.tagId = raw != null && raw !== '' ? Number(raw) : undefined;
					} else {
						base.tagId = Number(base.tagId);
					}
					if (base.step !== undefined && base.step !== null) {
						base.step = String(base.step);
					}
					this.form = { ...emptyForm(), ...base };
					this.messageCaret = {
						start: (this.form.message && this.form.message.length) || 0,
						end: (this.form.message && this.form.message.length) || 0,
					};
					if (this.$refs.adminForm) {
						this.$refs.adminForm.clearValidate();
					}
				});
			}, {
				immediate: true
			});
			this.unwatchVisible = this.$watch('visible', (val) => {
				if (val) {
					this.loadSceneTagList();
				}
			}, {
				immediate: true
			});
		},
		computed: {
			isUpdate: function() {
				return this.model && this.model.id > 0 ? true : false;
			}
		},
		methods: {
			initForm() {
				this.form = emptyForm();
				this.messageCaret = { start: 0, end: 0 };
				this.$nextTick(() => {
					if (this.$refs.adminForm) {
						this.$refs.adminForm.clearValidate();
					}
				});
			},
			handleOk() {
				this.$emit('ok');
			},
			handleCancel() {
				this.$emit('cancel');
				this.resetState();
			},
			resetState() {
			},
			getMessageTextareaEl() {
				const root = this.$refs.messageTextarea;
				if (!root) {
					return null;
				}
				const resizable = root.$refs && root.$refs.resizableTextArea;
				const native = resizable && resizable.$refs && resizable.$refs.textArea;
				return native || null;
			},
			syncMessageCaret() {
				const el = this.getMessageTextareaEl();
				if (!el || typeof el.selectionStart !== 'number') {
					return;
				}
				this.messageCaret = {
					start: el.selectionStart,
					end: el.selectionEnd,
				};
			},
			insertTemplateVariable(token) {
				const msg = this.form.message == null ? '' : String(this.form.message);
				let start = this.messageCaret.start;
				let end = this.messageCaret.end;
				if (start == null || end == null || start < 0 || end < 0) {
					start = end = msg.length;
				}
				if (start > msg.length) {
					start = msg.length;
				}
				if (end > msg.length) {
					end = msg.length;
				}
				const next = msg.slice(0, start) + token + msg.slice(end);
				this.form.message = next;
				const caret = start + token.length;
				this.messageCaret = { start: caret, end: caret };
				this.$nextTick(() => {
					const el = this.getMessageTextareaEl();
					if (el) {
						el.focus();
						el.setSelectionRange(caret, caret);
					}
				});
			},
			loadSceneTagList() {
				this.sceneTagFetching = true;
				return accostSceneTagSuggest().then((res) => {
					if (!res.isSucceed) {
						return;
					}
					const list = res.data.recordList || [];
					let options = list.map((row) => ({
						id: Number(row.id),
						name: row.name,
						depict: row.depict != null ? String(row.depict) : '',
					}));
					const selected = this.form.tagId;
					if (selected != null && selected !== '' && !Number.isNaN(Number(selected)) &&
						!options.some((item) => Number(item.id) === Number(selected))) {
						const displayName = (this.model && (this.model.name || this.model.tagName))
							? (this.model.name || this.model.tagName)
							: ('#' + selected);
						options = [{ id: Number(selected), name: displayName, depict: '' }].concat(options);
					}
					this.sceneTagOptions = options;
				}).catch(() => {}).finally(() => {
					this.sceneTagFetching = false;
				});
			},
			filterSceneTagOption(input, option) {
				const q = (input || '').trim().toLowerCase();
				if (!q) {
					return true;
				}
				const props = option.componentOptions && option.componentOptions.propsData;
				const id = props && props.value !== undefined ? props.value : undefined;
				const rec = this.sceneTagOptions.find((x) => String(x.id) === String(id));
				const hay = rec
					? (String(rec.name) + ' ' + String(rec.depict || '')).toLowerCase()
					: '';
				return hay.indexOf(q) >= 0;
			},
		}
	}
</script>

<style scoped>
	.ant-input-number-handler-wrap :deep(.ant-input-number-handler-wrap) {
		display: none;
	}

	.template-var-panel {
		padding: 8px 0 8px 16px;
		border-left: 1px solid #e8e8e8;
		min-height: 280px;
	}

	.template-var-panel-title {
		font-size: 15px;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.85);
		margin-bottom: 8px;
	}

	.template-var-panel-hint {
		font-size: 12px;
		color: rgba(0, 0, 0, 0.45);
		margin-bottom: 16px;
		line-height: 1.5;
	}

	.template-var-row {
		margin-bottom: 14px;
	}

	.template-var-tokens {
		line-height: 1.8;
	}

	.template-var-token {
		display: inline-block;
		margin-right: 8px;
		margin-bottom: 4px;
		padding: 2px 6px;
		font-family: Consolas, Monaco, 'Courier New', monospace;
		font-size: 12px;
		color: #1890ff;
		background: #e6f7ff;
		border-radius: 2px;
		cursor: pointer;
		user-select: none;
	}

	.template-var-token:hover {
		background: #bae7ff;
	}

	.template-var-desc {
		font-size: 12px;
		color: rgba(0, 0, 0, 0.45);
		margin-top: 2px;
	}
</style>
